import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Member } from '../types/member';
import ExpensesList from './ExpensesList';
import SettlementResults from './SettlementResults';
import { icons } from '../assets/icons';
import { CurrencyCode } from '../constants/currencies';
import { useGroupExpenses } from '../hooks/queries/useGroupExpenses';
import { useAllExpenseParticipations } from '../hooks/queries/useAllExpenseParticipations';
import { useGroupedExpenses } from '../hooks/useGroupedExpenses';
import { calculateSettlement } from '../utils/settlementCalculator';

interface GroupDetailTabsProps {
  groupId: string;
  currency: CurrencyCode;
  currentUser: any;
  currentUserMemberId: string;
  members: Member[];
  memberNameMap: Map<string, string>;
  treasurerMemberId?: string | null;
  isMemberOfGroup: boolean;
  openInviteModal: () => void;
  isLoading: boolean;
}

const GroupDetailTabs: React.FC<GroupDetailTabsProps> = ({
  groupId,
  currency,
  currentUser,
  currentUserMemberId,
  members,
  memberNameMap,
  treasurerMemberId=null,
  isMemberOfGroup,
  openInviteModal,
  isLoading=false,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('activeTab') || 'expenses';

  // 데이터 페칭
  const { data: rawExpenses, isLoading: expensesLoading, isError: expensesError } = useGroupExpenses(groupId, currentUser, isMemberOfGroup);
  const { data: allExpenseParticipations, isLoading: participationsLoading, isError: participationsError } = useAllExpenseParticipations(groupId, isMemberOfGroup);

  // 데이터 재구성 (memoized)
  const groupedExpenses = useGroupedExpenses(rawExpenses);
  
  const settlementData = useMemo(() => {
    return calculateSettlement(allExpenseParticipations, currentUserMemberId, members, treasurerMemberId || undefined, rawExpenses);
  }, [allExpenseParticipations, rawExpenses, currentUserMemberId, members, treasurerMemberId]);

  // 각 탭별 로딩 상태
  const expensesTabLoading = expensesLoading || isLoading;
  const settlementTabLoading = expensesLoading || participationsLoading || isLoading;
  
  return (
    <>
      {/* 탭 메뉴 */}
      <div className="rounded-xl p-1.5 select-none w-full flex justify-around items-center bg-gray-50 mb-6">
        <button
          className={`w-full py-2 text-sm rounded-lg font-bold ${activeTab === 'expenses' ? 'text-white bg-blue-500' : 'text-gray-800'}`}
          onClick={() => setSearchParams({ activeTab: 'expenses' }, { replace: true })}
        >
          지출 내역
        </button>
        <button
          className={`w-full py-2 text-sm rounded-lg font-bold flex items-center justify-center gap-1 ${activeTab === 'settlement' ? 'text-white bg-blue-500' : 'text-gray-800'}`}
          onClick={() => setSearchParams({ activeTab: 'settlement' }, { replace: true })}
        >
          정산 결과
        </button>
      </div>
      
      {/* 탭 내용 */}
      {activeTab === 'expenses' ? (
        <ExpensesList 
          groupCurrency={currency} 
          memberNameMap={memberNameMap} 
          currentUserMemberId={currentUserMemberId}
          groupedExpenses={groupedExpenses}
          rawExpenses={rawExpenses}
          isLoading={expensesTabLoading}
          isError={expensesError}
        />
      ) : (
        <SettlementResults 
          groupId={groupId}
          groupCurrency={currency} 
          currentMemberId={currentUserMemberId} 
          members={members} 
          memberNameMap={memberNameMap} 
          treasurerMemberId={treasurerMemberId || undefined}
          openInviteModal={openInviteModal}
          allExpenseParticipations={allExpenseParticipations}
          settlementData={settlementData}
          isLoading={settlementTabLoading}
          isError={participationsError || expensesError}
        />
      )}

      {
        isMemberOfGroup && activeTab === 'expenses' &&
        // 지출 추가 버튼
          <button
            onClick={ () => {

              navigate(`/groups/${groupId}/add-expense?currency=${currency}`);
            }}
            className="fixed bottom-8 left-1/2 transform -translate-x-1/2 w-18 h-18 bg-blue-500 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
            aria-label="지출 추가하기"
          >
          <img src={icons.plus} className="w-8 h-8" />
        </button>
      }
    </>
  );
};

export default GroupDetailTabs;
